import { supabase } from '../config/supabase';

export const adminService = {
  // Create user directly using Supabase Edge Function
  async createUser(email, username, fullName, password, isAdmin = false) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    // Get Supabase URL from environment or use the configured client
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://wljyalsyitroeaotwvdi.supabase.co';

    const response = await fetch(`${supabaseUrl}/functions/v1/create-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        email,
        username,
        fullName,
        password,
        isAdmin
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to create user');
    }

    return data;
  },

  // Test email configuration
  async testEmail(email) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const response = await fetch('/api/users/test-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ email })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to send test email');
    }

    return data;
  },


  // Get all users (profiles only - auth data not accessible from client)
  async getUsers() {
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    // Transform data to match expected format
    return profiles.map(profile => ({
      id: profile.id,
      username: profile.username,
      fullName: profile.full_name || profile.username,
      email: profile.email || 'N/A', // Use email from profile if available
      isAdmin: profile.is_admin,
      isSuperAdmin: profile.is_super_admin,
      clickCount: profile.click_count || 0,
      loginCount: profile.login_count || 0,
      lastLogin: profile.updated_at, // Use profile update time as approximation
      createdAt: profile.created_at,
      updatedAt: profile.updated_at
    }));
  },

  // Update user role
  async updateUserRole(userId, isAdmin) {
    const { data, error } = await supabase
      .from('profiles')
      .update({ is_admin: isAdmin })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Reset user statistics
  async resetUserStats(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .update({ 
        click_count: 0,
        login_count: 0
      })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Delete user completely with all associated data
  async deleteUser(userId) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const response = await fetch(`/api/users/${userId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete user');
    }

    return data;
  },

  // Get user statistics
  async getUserStatistics() {
    // Get overview stats
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('is_admin, is_super_admin, click_count, login_count, created_at');

    if (profilesError) throw profilesError;

    const totalUsers = profiles.length;
    const adminUsers = profiles.filter(p => p.is_admin).length;
    const superAdminUsers = profiles.filter(p => p.is_super_admin).length;
    
    // Active users (logged in within 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const { data: activeUsers, error: activeError } = await supabase
      .from('profiles')
      .select('id')
      .gte('updated_at', thirtyDaysAgo.toISOString());

    if (activeError) throw activeError;

    // Most active users
    const mostActiveUsers = profiles
      .sort((a, b) => (b.click_count || 0) - (a.click_count || 0))
      .slice(0, 5)
      .map(p => ({
        id: p.id,
        username: p.username,
        clickCount: p.click_count || 0,
        loginCount: p.login_count || 0
      }));

    // Recent logins (mock data since we need auth.users access)
    const recentLogins = profiles
      .sort((a, b) => (b.login_count || 0) - (a.login_count || 0))
      .slice(0, 5)
      .map(p => ({
        id: p.id,
        username: p.username,
        lastLogin: p.updated_at, // Approximation
        loginCount: p.login_count || 0
      }));

    return {
      overview: {
        totalUsers,
        activeUsers: activeUsers.length,
        adminUsers,
        superAdminUsers
      },
      mostActiveUsers,
      recentLogins
    };
  },

  // Get user activity details
  async getUserActivity(userId) {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) throw error;

    // Return profile data (auth data not accessible from client)
    return {
      id: profile.id,
      username: profile.username,
      email: 'N/A', // Email not accessible from client side
      isAdmin: profile.is_admin,
      isSuperAdmin: profile.is_super_admin,
      clickCount: profile.click_count || 0,
      loginCount: profile.login_count || 0,
      lastLogin: profile.updated_at, // Use profile update time as approximation
      createdAt: profile.created_at,
      updatedAt: profile.updated_at
    };
  },

};

export default adminService;
